const { jQuery: $ } = window;

/**
 * Owns the mutable state for a single shortcode instance, backed by `$el.data("settings")` (the canonical DOM
 * mirror, so anything reading the element's jQuery data keeps working); `cache` and `requiresReload` live here
 * as fields. Server-seeded settings are read from the form's `<script class="kpfsc__settings">` JSON block,
 * falling back to the legacy `data-settings` attribute (see {@link read}). The settings object keeps its
 * historical shape, so the browser-history serialization format is unchanged.
 */
export default class StateStore {
    /** @member {jQuery} */
    #$el;

    /** @member {object[]} Posts fetched so far (travels in browser history for back/forward). */
    cache = [];

    /** @member {boolean} Whether the current selection differs from defaults enough to require a reload. */
    requiresReload = false;

    /**
     * Resolves a form's server-seeded settings: the `<script class="kpfsc__settings">` JSON block if present
     * (its parsed object is cached into `$el.data("settings")` for later reads), else the legacy
     * `data-settings` attribute. Idempotent.
     *
     * @param {jQuery} $el The shortcode form element.
     * @returns {object|undefined}
     */
    static read($el) {
        // Direct child only — never a nested shortcode's block.
        const $script = $el.children("script.kpfsc__settings").first();

        if($script.length) {
            try {
                const parsed = JSON.parse($script.text());
                $el.data("settings", parsed);
                return parsed;
            } catch (e) {
                // Malformed JSON — fall through to the attribute.
            }
        }

        return $el.data("settings"); // jQuery lazily parses the data-settings attribute (fallback)
    }

    /**
     * @param {jQuery} $el The shortcode form element.
     */
    constructor($el) {
        this.#$el = $el;
        StateStore.read($el); // seed the jQuery data cache from the script block (or leave the attribute)
    }

    /**
     * @returns {jQuery}
     */
    get $el() {
        return this.#$el;
    }

    /**
     * @returns {string} The shortcode element id.
     */
    get id() {
        return this.#$el.attr("id");
    }

    /**
     * Reads the whole settings object, or a single property when a name is given.
     *
     * @param {string|null} property
     * @returns {*}
     */
    get(property = null) {
        const settings = this.#$el.data("settings");

        if(property === null) {
            return settings;
        }

        return settings[property];
    }

    /**
     * Writes a single settings property, keeping the DOM mirror in sync.
     *
     * @param {string} property
     * @param {*} value
     * @returns {StateStore}
     */
    set(property, value) {
        const settings = this.#$el.data("settings");
        settings[property] = value;
        this.#$el.data("settings", settings);
        return this;
    }

    /**
     * Replaces the entire settings object.
     *
     * @param {object} settings
     * @returns {StateStore}
     */
    replace(settings) {
        this.#$el.data("settings", settings);
        return this;
    }

    /**
     * Produces the serializable state for browser history.
     *
     * @returns {{settings: object, cache: object[], requiresReload: boolean}}
     */
    serialize() {
        return {
            settings: this.get(),
            cache: this.cache,
            requiresReload: this.requiresReload,
        };
    }

    /**
     * Restores state from a history entry.
     *
     * @param {{settings: object, cache: object[], requiresReload: boolean}} state
     * @returns {StateStore}
     */
    restore(state) {
        this.replace(state.settings);
        this.cache = state.cache || [];
        this.requiresReload = state.requiresReload || false;
        return this;
    }
}
